import { getInitialEdges } from "features/entities/EntityCreatePage/EntityGraph/edges";
import { getUniqueInvestingEdgeKey } from "features/entities/EntityCreatePage/EntityGraph/edges/edgeUtils";
import { INVESTOR_GROUP_PREFIX } from "features/entities/EntityCreatePage/EntityGraph/entityGraphConstants";
import { getInitialNodes } from "features/entities/EntityCreatePage/EntityGraph/nodes";
import { Entity } from "features/entities/EntityCreatePage/EntityGraph/nodes/types";
import { useGetAllocationBasisNestedDataSuspense } from "gen/endpoints/nested-allocation-basis/nested-allocation-basis";
import { useSecurityOwnershipWithEntitiesRetrieveSuspense } from "gen/endpoints/security-ownership/security-ownership";
import { NestedAllocation } from "gen/model/nestedAllocation";
import entries from "lodash-es/entries";
import groupBy from "lodash-es/groupBy";
import mapValues from "lodash-es/mapValues";
import values from "lodash-es/values";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export const useInitialGraphNodesEdges = (fundFamilyId: string) => {
  const { data: securityOwnerships } =
    useSecurityOwnershipWithEntitiesRetrieveSuspense(fundFamilyId);
  const {
    data: { nested_allocation_by_entity_id: allocationMap },
  } = useGetAllocationBasisNestedDataSuspense(fundFamilyId);

  const { t } = useTranslation(["common", "entity"]);

  const { securityOwnershipGroupedByOwnedId, idToGraphEntity } = useMemo(() => {
    const idToGraphEntity = securityOwnerships.reduce<Record<string, Entity>>(
      (acc, curr) => {
        acc[curr.owning_entity_id] = {
          id: curr.owning_entity_id,
          functional_type: curr.owning_entity_functional_type,
          legal_name: curr.owning_entity_legal_name,
        };
        acc[curr.owned_entity_id] = {
          id: curr.owned_entity_id,
          functional_type: curr.owned_entity_functional_type,
          legal_name: curr.owned_entity_legal_name,
        };
        return acc;
      },
      {},
    );

    const securityOwnershipGroupedByOwnedId = mapValues(
      groupBy(
        securityOwnerships.filter((sec) => {
          const investor = sec.owning_entity_id;
          const owner = idToGraphEntity[investor];
          return owner.functional_type === "investor";
        }),
        (security) => {
          const investedInTo = security.owned_entity_id;
          return investedInTo;
        },
      ),
      (securities) => ({
        investorsGroupId: `${INVESTOR_GROUP_PREFIX}-${securities[0].owned_entity_id}`,
        label: t("entity:create.lp_group_label", {
          name: idToGraphEntity[securities[0].owned_entity_id].legal_name,
        }),
        groupOfSecurityIds: new Set(
          securities.map((sec) => sec.security_ownership_id),
        ),
      }),
    );

    return {
      securityOwnershipGroupedByOwnedId,
      idToGraphEntity,
    };
  }, [securityOwnerships, t]);

  const edgeWeights = useMemo(() => {
    const recurseOverAllocationsToBuildEdgeWeight = (
      map: {
        [key: string]: NestedAllocation;
      },
      edgeWeights: Record<string, number> = {},
    ): Record<string, number> => {
      entries(map).forEach(([security_ownership_id, weight]) => {
        const security_ownership = securityOwnerships.find(
          (sec) => sec.security_ownership_id === security_ownership_id,
        );
        if (security_ownership) {
          // sum up the weights between the same investing path
          // LPs are grouped by the entity they are investing in
          const key = getUniqueInvestingEdgeKey(
            {
              owning_entity_id: security_ownership.owning_entity_id,
              owned_entity_id: security_ownership.owned_entity_id,
              security_ownership_id,
            },
            securityOwnershipGroupedByOwnedId,
            idToGraphEntity,
          );
          edgeWeights[key] =
            (edgeWeights[key] || 0) + Number(weight.allocation_percentage);
        }

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (weight.nested_allocations) {
          recurseOverAllocationsToBuildEdgeWeight(
            weight.nested_allocations,
            edgeWeights,
          );
        }
      });
      return edgeWeights;
    };
    return recurseOverAllocationsToBuildEdgeWeight(allocationMap);
  }, [
    allocationMap,
    securityOwnerships,
    securityOwnershipGroupedByOwnedId,
    idToGraphEntity,
  ]);

  return useMemo(
    () => ({
      initalNodes: getInitialNodes(
        values(idToGraphEntity),
        securityOwnershipGroupedByOwnedId,
      ),
      initialEdges: getInitialEdges(
        securityOwnerships,
        edgeWeights,
        idToGraphEntity,
        securityOwnershipGroupedByOwnedId,
      ),
    }),
    [
      idToGraphEntity,
      securityOwnerships,
      securityOwnershipGroupedByOwnedId,
      edgeWeights,
    ],
  );
};
